import { Alert, InputAdornment } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { PatternFormat } from "react-number-format";
import Input from "@/components/Tags/Input";
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from "react";

import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import axios from "@/lib/axios";
import LoadingButton from "@/components/LoadingButton";
import Button from "@/components/Tags/Form/Button";
import Turnstile from "@/components/Tags/Turnstile";

const defaultValues = {
    company: '',
    contact_person: '',
    contact_no: '',
    email: '',
    est_no_units: '',
};

const COMPANY_MAX_LENGTH = 150;
const NAME_MAX_LENGTH = 80;
const MAX_UNITS = 100000;

// Must match CAPTCHA_ACTIONS['partner'] in api-rapideph's PageController.
const CAPTCHA_ACTION = 'partner_inquiry';

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
        <PatternFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values, sourceInfo) => {
                if(values.value){
                    onChange(values.value);
                }
            }}
            format="### ### ####"
            allowEmptyFormatting mask="-"
        />
    );
});

NumberFormatCustom.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

export default function BeAPartner(){
    const { handleSubmit, reset, setValue, setError, clearErrors, getValues, control, trigger, formState: { errors, isValid } } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: defaultValues
     });

    const [loading, setLoading] = useState(false);
    const [alertStatus, setAlertStatus] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);

    // Bot protection (GPH-525, same as the franchise form): invisible check, hidden trap field, and how long the form was open.
    const [captchaToken, setCaptchaToken] = useState('');
    const [captchaUnavailable, setCaptchaUnavailable] = useState(false);
    const turnstileRef = useRef(null);
    const honeypotRef = useRef(null);
    const loadedAtRef = useRef(null);
    const submittingRef = useRef(false);

    useEffect(() => {
        loadedAtRef.current = Date.now();
    }, []);

    const handleCaptchaUnavailable = useCallback(() => setCaptchaUnavailable(true), []);

    const onSubmit = async (data) => {
        // A double click can land before the button re-renders as disabled.
        if(submittingRef.current){
            return;
        }
        // The invisible check usually finishes long before anyone can fill the form.
        // If it can't load at all, send anyway and let the server decide.
        if(!captchaToken && !captchaUnavailable){
            setAlertStatus(203);
            setAlertMessage('We\'re still verifying your browser. Please try again in a moment.');
            return;
        }

        submittingRef.current = true;
        setLoading(true);

        const payload = {
            ...data,
            captcha_token: captchaToken,
            website: honeypotRef.current?.value ?? '',
            form_elapsed_ms: Date.now() - loadedAtRef.current,
        };

        try {
            await axios.get('/sanctum/csrf-cookie');
            // The API is on another subdomain, so axios only sends the CSRF header when asked.
            const response = await axios.post(process.env.API_URL+`/be_a_partner`, payload, { withXSRFToken: true });
            setAlertStatus(response.data.status);
            setAlertMessage(response.data.message);
        } catch (error) {
            const fieldErrors = Object.entries(error?.response?.data?.errors ?? {})
                                    .filter(([field]) => field in defaultValues);
            if(fieldErrors.length){
                // Show server-side rule failures on the fields themselves.
                fieldErrors.forEach(([field, messages]) => {
                    setError(field, { type: 'server', message: messages[0] });
                });
                setAlertStatus(null);
            }
            else{
                setAlertStatus(203);
                setAlertMessage(error?.response?.data?.message ?? 'Connection Failed. Try again.');
            }
        }

        // Tokens are single-use, so fetch a fresh one for any retry.
        turnstileRef.current?.reset();
        submittingRef.current = false;
        setLoading(false);
    };

    const onError = (data) => {
        console.log('Invalid Form!');
    };
    return(
        <div className='max-w-screen-xl mx-auto bg-white pt-12'>
            <div className='max-w-screen-lg mx-auto px-8'>
                <div className='relative'>
                    <div className='absolute -bottom-4 h-1.5 w-32 bg-rapide-yellow'></div>
                    <h1 className='text-4xl'>Be <span className='font-semibold'>part of a rapidly growing P15 Billion Industry!</span></h1>
                </div>
                <p className='text-xl pt-12 leading-9 font-light'>
                    Your trusted auto maintenance service partner. Here at Rapidé, we are always on the lookout for our next great franchisees. Do you have what it takes to be part of a 15 billion industry?
                </p>
            </div>
            <div className='relative grid sm:grid-cols-2 grid-cols-1 sm:px-6 px-4 pt-4 pb-20'>
                <div className='px-4 pt-4'>
                    {alertStatus && (
                        <Alert severity={`${(alertStatus  == 200) ? 'success' : 'error'}`}>
                            {alertMessage}
                        </Alert>
                    )}
                    <form className="mt-2" onSubmit={handleSubmit(onSubmit, onError)}>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="company"
                                        name="company"
                                        label="Company"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        error={errors.company != null}
                                        helperText={errors.company && errors.company?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        inputProps={{ maxLength: COMPANY_MAX_LENGTH }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start"><BusinessOutlinedIcon/></InputAdornment>,
                                        }}
                                    />
                                )
                            }}
                            name="company"
                            control={control}
                            rules={{
                                required: 'Please enter your company name',
                                validate: (value) => value.trim().length >= 2 || 'Please enter your company name.',
                                maxLength: {
                                    value: COMPANY_MAX_LENGTH,
                                    message: 'Please enter your company name.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="text"
                                        id="contact_person"
                                        name="contact_person"
                                        label="Contact Person"
                                        variant="filled"
                                        size="small"
                                        error={errors.contact_person != null}
                                        helperText={errors.contact_person && errors.contact_person?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        inputProps={{ maxLength: NAME_MAX_LENGTH }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start"><Person2OutlinedIcon/></InputAdornment>,
                                        }}
                                    />
                                )
                            }}
                            name="contact_person"
                            control={control}
                            rules={{
                                required: 'Please enter your contact person',
                                // First and last name, as the server requires.
                                validate: (value) => /\S\s+\S/.test(value.trim()) || 'Please enter the contact person\'s full name.',
                                maxLength: {
                                    value: NAME_MAX_LENGTH,
                                    message: 'Please enter the contact person\'s full name.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="text"
                                        id="contact_no"
                                        name="contact_no"
                                        label="Contact Number"
                                        variant="filled"
                                        size="small"
                                        error={errors.contact_no != null}
                                        helperText={errors.contact_no && errors.contact_no?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start">+63</InputAdornment>,
                                            inputComponent: NumberFormatCustom,
                                        }}
                                    />
                                )
                            }}
                            name="contact_no"
                            control={control}
                            rules={{
                                required: "Please enter your mobile number",
                                minLength: {
                                    value: 10,
                                    message: 'Invalid mobile number.'
                                },
                                maxLength: {
                                    value: 10,
                                    message: 'Invalid mobile number.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="email"
                                        id="email"
                                        name="email"
                                        label="Email"
                                        variant="filled"
                                        size="small"
                                        error={errors.email != null}
                                        helperText={errors.email && errors.email?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start"><EmailOutlinedIcon/></InputAdornment>,
                                        }}
                                    />
                                )
                            }}
                            name="email"
                            control={control}
                            rules={{
                                required: 'Please enter your email address',
                                pattern: {
                                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                    message: 'Please enter a valid email address.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="number"
                                        id="est_no_units"
                                        name="est_no_units"
                                        label="Estimated No. of Units"
                                        variant="filled"
                                        size="small"
                                        error={errors.est_no_units != null}
                                        helperText={errors.est_no_units && errors.est_no_units?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        inputProps={{ min: 1, max: MAX_UNITS, step: 1 }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="est_no_units"
                            control={control}
                            rules={{
                                required: 'Please enter your Estimated No. of Units',
                                // Whole number from 1 to MAX_UNITS, as the server requires.
                                validate: (value) => (/^\d+$/.test(String(value).trim()) && Number(value) >= 1 && Number(value) <= MAX_UNITS) || 'Please enter the estimated number of units.',
                            }}
                        />
                        {/* Honeypot: off-screen and hidden from screen readers and the tab order. People never fill it; bots do. */}
                        <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
                            <label htmlFor="website">Website</label>
                            <input
                                ref={honeypotRef}
                                type="text"
                                id="website"
                                name="website"
                                tabIndex={-1}
                                aria-hidden="true"
                                autoComplete="off"
                                defaultValue=""
                            />
                        </div>
                        <Turnstile
                            ref={turnstileRef}
                            action={CAPTCHA_ACTION}
                            onVerify={setCaptchaToken}
                            onUnavailable={handleCaptchaUnavailable}
                        />
                        <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                            <div className="md:col-start-2 col-start-1">
                                <Button type="submit" loading={loading} label="Submit" llabel="Submitting..." />
                            </div>
                        </div>
                    </form>
                </div>
                <div className='px-4 pt-4'>
                    <h1 className="text-2xl font-bold tracking-wider">DOCUMENTARY REQUIREMENTS</h1>
                    <ul className="list-disc list-outside w-full pl-6 font-light">
                        <li className="w-full">
                            <p>Two (2) years latest ITR and Audited FS with BIR Stamped</p>
                            <p>Two (2) years In-house FS (latest)</p>
                            <p>Bank Statements for the last three (3) months of at least two (2) depository banks</p>
                        </li>
                        <li>
                            <p>Mayor / Business Permit (2 years latest)</p>
                            <p>DTI Registration (if Sole Prop.)</p>
                        </li>
                        <li>
                            <p>SEC Registration</p>
                        </li>
                        <li>
                            <p>Articles of Incorporation and By Laws</p>
                        </li>
                        <li>
                            <p>Latest General Information Sheet (GIS)</p>
                        </li>
                        <li>
                            <p>Company Profile</p>
                        </li>
                        <li>
                            <p>Two (2) valid / Government ID&rsquo;s (with picture)</p>
                        </li>
                        <li>
                            <p>List of Company Vehicles with Counter Signature</p>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    )
}